import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { SharepointConfigurationsRepository } from '../../../infrastructure/repositories/sharepoint-configurations.repository';
import { SharepointConfigurationsState } from '../../states/sharepoint-configurations.state';
import { GetSharepointConfigurationByIdResult } from './get-sharepoint-configuration-by-id.result';

/**
 * Get SharePoint Configuration by ID use case.
 * Retrieves the SharePoint configuration for the current tenant and updates state.
 */
@Injectable({ providedIn: 'root' })
export class GetSharepointConfigurationByIdUseCase {
  private readonly _repository = inject(SharepointConfigurationsRepository);
  private readonly _state = inject(SharepointConfigurationsState);

  /**
   * Executes the get SharePoint configuration by ID use case.
   * Retrieves configuration for the current tenant (no ID needed).
   * @returns Observable with result
   */
  public execute(): Observable<GetSharepointConfigurationByIdResult> {
    this._state.setLoadingSharepointConfiguration(true);
    this._state.setErrorSharepointConfiguration(null);

    return this._repository.getSharepointConfiguration().pipe(
      tap((data) => {
        this._state.setSharepointConfiguration(data);
        this._state.setLoadingSharepointConfiguration(false);
      }),
      map((data) => ({ data })),
      catchError((error) => {
        // 404 is expected when no configuration exists yet
        if (error.status === 404) {
          this._state.setSharepointConfiguration(null);
          this._state.setLoadingSharepointConfiguration(false);
          return of({ data: null });
        }
        this._state.setErrorSharepointConfiguration(error.message || 'Failed to load SharePoint configuration');
        this._state.setLoadingSharepointConfiguration(false);
        throw error;
      }),
    );
  }
}
